import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@chargify/formatters';
import {
  Alert,
  BodyText,
  Box,
  Column,
  Heading,
  Row,
  SummaryTable,
} from '@chargify/custom-ui-components';

import { AllocationsPreview, SerializedComponent } from '../../machines';
import {
  getAllocationSummaryPaymentMessage,
  mapChargeSummaryToTable,
  sortComponentAllocations,
} from './utils';
import AllocationsTable from './components/AllocationsTable';
import { Summary } from '../Summary';
import { TotalCharge } from '../TotalCharge';

export type ComponentsAllocationPreviewProps = {
  /**
   * Allocations preview
   */
  allocationsPreview: AllocationsPreview;
  /**
   * Allocations components
   */
  components: SerializedComponent[];
};

const ComponentsAllocationPreview = ({
  allocationsPreview,
  components,
}: ComponentsAllocationPreviewProps) => {
  const { t, i18n } = useTranslation(['subscriptions-manager']);

  const paymentSummaryMessage = getAllocationSummaryPaymentMessage({
    accrueCharge: allocationsPreview.accrueCharge,
    totalAmountInCents: allocationsPreview.totalInCents,
    currency: allocationsPreview.currency,
    t,
    language: i18n.language,
  });

  const sortedAllocations = sortComponentAllocations(
    allocationsPreview.componentsAllocations,
    components
  );
  return (
    <div>
      <Box marginBottom={6}>
        <Heading variant="h4">
          {t('components_allocations.changes_and_charges_summary')}
        </Heading>
      </Box>
      {sortedAllocations.recurringAllocations.length > 0 && (
        <AllocationsTable
          heading={t('components_allocations.recurring_charges')}
          componentsAllocations={sortedAllocations.recurringAllocations}
          currency={allocationsPreview.currency}
        />
      )}
      {sortedAllocations.oneTimeAllocations.length > 0 && (
        <AllocationsTable
          heading={t('components_allocations.one_time_charges')}
          componentsAllocations={sortedAllocations.oneTimeAllocations}
          currency={allocationsPreview.currency}
        />
      )}
      <Summary paddingLeft={5} paddingRight={5} paddingTop={2} marginBottom={5}>
        <TotalCharge>
          <SummaryTable
            rows={mapChargeSummaryToTable({
              allocationsPreview,
              t,
              language: i18n.language,
            })}
            columnSpans={[12, 12]}
          />
        </TotalCharge>
        <Box paddingTop={3} paddingBottom={4}>
          <Row>
            <Column span={12}>
              <BodyText variant="body1" color="success.main" fontWeight={3}>
                {t('components_allocations.total_charge')}
              </BodyText>
            </Column>
            <Column span={12}>
              <BodyText
                variant="body1"
                color="success.main"
                textAlign="right"
                fontWeight={3}
              >
                {formatCurrency(
                  allocationsPreview.currency,
                  allocationsPreview.totalInCents > 0
                    ? allocationsPreview.totalInCents
                    : 0,
                  i18n.language
                )}
              </BodyText>
            </Column>
          </Row>
        </Box>
      </Summary>
      <Alert title={paymentSummaryMessage.title} icon="receipt" color="success">
        <Alert.TextMessage>
          {paymentSummaryMessage.description}
        </Alert.TextMessage>
      </Alert>
    </div>
  );
};

export default ComponentsAllocationPreview;
